/** @jsxImportSource @emotion/react */
import type { ReactElement } from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';
import { ODSButton, ODSGroup, ODSLoginPortal } from 'bc-ods';
import { confirmDodConsent } from 'api-client/accounts';
import type { AuthAccountWithPassType } from 'api-client/accounts';
import { logoutUser } from 'api-client';
import sameVersion from 'stores/compare/versions';
import useAccountStore from 'stores/useAccountStore';
import moment from 'moment';
import { DATETIME_FORMAT_V2 } from 'utils/datetime-utils';
import { useNavigate } from 'react-router-dom';

function DodConsent(props: { loadUser: () => void }): ReactElement {
  const messages = defineMessages({
    logout: {
      id: 'DodConsent.logout',
      defaultMessage: 'Log out',
    },
    accept: {
      id: 'DodConsent.accept',
      defaultMessage: 'Accept and continue',
    },
    dodConsentIntro: {
      id: 'DodConsent.intro',
      defaultMessage: `
        You are accessing a U.S. Government (USG) Information System (IS) that is provided 
        for USG-authorized use only. By using this IS (which includes any device attached 
        to this IS), you consent to the following conditions:
      `,
    },
    dodConsentOne: {
      id: 'DodConsent.one',
      defaultMessage: `
        The USG routinely intercepts and monitors communications on this IS for purposes
        including, but not limited to, penetration testing, COMSEC monitoring, network
        operations and defense, personnel misconduct (PM), law enforcement (LE), and
        counterintelligence (CI) investigations.
      `,
    },
    dodConsentTwo: {
      id: 'DodConsent.two',
      defaultMessage: `
        At any time, the USG may inspect and seize data stored on this IS.
      `,
    },
    dodConsentThree: {
      id: 'DodConsent.three',
      defaultMessage: `
        Communications using, or data stored on, this IS are not private, are subject to
        routine monitoring, interception, and search, and may be disclosed or used for any
        USG-authorized purpose.
      `,
    },
    dodConsentFour: {
      id: 'DodConsent.four',
      defaultMessage: `
        This IS includes security measures (e.g., authentication and access controls) to
        protect USG interests--not for your personal benefit or privacy.
      `,
    },
    dodConsentFive: {
      id: 'DodConsent.five',
      defaultMessage: `
        Notwithstanding the above, using this IS does not constitute consent to PM, LE or CI
        investigative searching or monitoring of the content of privileged communications,
        or work product, related to personal representation or services by attorneys,
        psychotherapists, or clergy, and their assistants. Such communications and work
        product are private and confidential. See User Agreement for details.
      `,
    },
    lastSignIn: {
      id: 'DodConsent.lastSignIn',
      defaultMessage: 'Last signed in: {lastSignIn}',
    },
  });

  const navigate = useNavigate();
  const user = useAccountStore(
    ({ account }) => account ?? ({} as AuthAccountWithPassType),
    sameVersion
  );

  const handleLogout = () => {
    logoutUser();
  };

  const handleAccept = async () => {
    await confirmDodConsent();
    await props.loadUser();
    navigate('/');
  };

  return (
    <ODSLoginPortal
      subTitle={
        user.lastSignIn && (
          <em>
            <FormattedMessage
              {...messages.lastSignIn}
              values={{
                lastSignIn: moment.utc(user.lastSignIn).format(DATETIME_FORMAT_V2),
              }}
            />
          </em>
        )
      }
      data-testid="dod-consent"
      condensed
    >
      <div className="border-top border-bottom bg-light py-3 mb-4 px-4 lh-sm mx-n4">
        <p className="fw-medium">
          <FormattedMessage {...messages.dodConsentIntro} />
        </p>
        <ul className="mb-0" css={(theme) => ({ li: { ...theme.spacing.mt(2) } })}>
          <li>
            <FormattedMessage {...messages.dodConsentOne} />
          </li>
          <li>
            <FormattedMessage {...messages.dodConsentTwo} />
          </li>
          <li>
            <FormattedMessage {...messages.dodConsentThree} />
          </li>
          <li>
            <FormattedMessage {...messages.dodConsentFour} />
          </li>
          <li>
            <FormattedMessage {...messages.dodConsentFive} />
          </li>
        </ul>
      </div>
      <ODSGroup>
        <ODSButton quiet data-testid="cancel-consent" onClick={handleLogout}>
          <FormattedMessage {...messages.logout} />
        </ODSButton>
        <ODSButton cta fullWidth type="submit" data-testid="accept-consent" onClick={handleAccept}>
          <FormattedMessage {...messages.accept} />
        </ODSButton>
      </ODSGroup>
    </ODSLoginPortal>
  );
}

export default DodConsent;
